import { Button, useDisclosure } from "@heroui/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import {
  FaArrowRightToBracket,
  FaArrowsLeftRight,
  FaGears,
  FaUserPlus,
} from "react-icons/fa6";
import { api } from "@/api";
import useErrorHandling from "@/hooks/useErrorHandling";
import { LandlordUser } from "@/api/businessProfiles";
import { useNavigate } from "react-router-dom";
import { InviteEmployeeModal } from "@/app/components/modals/user/inviteUserModal";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { TransferOwnershipModal } from "@/app/components/modals/businessProfile/transferOwnershipModal";
import { useTour } from "@/hooks/useTour";
import {
  businessProfileLinks,
  Links,
} from "@/app/components/navigation/links/Links";

export function UsersPage() {
  const [users, setUsers] = useState<LandlordUser[] | null>(null);
  const navigate = useNavigate();

  const handleError = useErrorHandling();

  const fullscreenDisclosure = useDisclosure();
  const inviteDisclosure = useDisclosure();
  const transferOwnershipModalDisclosure = useDisclosure();

  useTour(9);

  useEffect(() => {
    api.businessProfiles
      .getLandlords()
      .then((response) => {
        setUsers(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, []);

  return (
    <>
      <InviteEmployeeModal disclosure={inviteDisclosure} />
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <Links links={businessProfileLinks("/dashboard/users")} />
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium" id="users-title">
                Сотрудники
              </span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                variant="bordered"
                startContent={<FaArrowsLeftRight />}
                color="primary"
                onPress={transferOwnershipModalDisclosure.onOpen}
              >
                Передать владение
              </Button>
              <Button
                startContent={<FaUserPlus />}
                color="primary"
                variant="bordered"
                className="max-w-fit"
                onClick={() => inviteDisclosure.onOpen()}
                id="create-user-button"
              >
                Пригласить
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <div id="users-table">
              <TableBuilder
                fullscreenDisclosure={fullscreenDisclosure}
                removeWrapper
                displayOptions={false}
                columns={[
                  {
                    label: "Имя пользователя",
                    key: "username",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Имя",
                    key: "first_name",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Фамилия",
                    key: "last_name",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Номер телефона",
                    key: "phone_number",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Telegram",
                    key: "telegram",
                    type: ColumnType.Custom,
                    render(value, _row) {
                      return (
                        <span>
                          {value == 0 || value == null
                            ? "Не привязан"
                            : "Привязан"}
                        </span>
                      );
                    },
                    sortable: true,
                  },
                  {
                    label: "Действия",
                    key: "actions",
                    type: ColumnType.Actions,
                    actions: [
                      {
                        icon: <FaArrowRightToBracket />,
                        onClick(_value, row) {
                          navigate("/dashboard/users/" + row.id);
                        },
                      },
                      {
                        icon: <FaGears />,
                        onClick(_value, row) {
                          navigate("/dashboard/users/" + row.id + "/roles");
                        },
                      },
                    ],
                  },
                ]}
                data={users || []}
                rowsPerPage={10}
                defaultSortDescriptor={{
                  column: "username",
                  direction: "ascending",
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <TransferOwnershipModal disclosure={transferOwnershipModalDisclosure} />
    </>
  );
}
